/**
 * FORQY Dock — the contextual help/assistant region (spec 2.2). The Dock knows where you are
 * (from the route) and offers curated help: what this area does, a step-by-step playbook,
 * keyboard shortcuts and patch notes. The free-text "ask FORQY" is deliberately NOT wired to an
 * invented backend — per the spec, secure knowledge-base answers come later; until then the Dock
 * surfaces real, contextual guidance only.
 */
export interface DockPlaybook {
  readonly title: string;
  readonly steps: readonly string[];
}

export interface DockContext {
  readonly area: string;
  readonly summary: string;
  readonly tips: readonly string[];
  readonly playbook?: DockPlaybook;
}

export interface DockShortcut {
  readonly keys: readonly string[];
  readonly label: string;
}

export interface DockPatchNote {
  readonly title: string;
  readonly body: string;
}

/** Keyed by the first German path segment (e.g. `vorgaenge` in `/vorgaenge/202600131`). */
const contextBySegment: Readonly<Record<string, DockContext>> = {
  dashboard: {
    area: 'Start & Dashboard',
    summary: 'Dein rollen- und mandantenbezogener Einstieg mit Kennzahlen und offenen Aufgaben.',
    tips: [
      'Die Kacheln zeigen Kennzahlen für den aktiven Mandanten.',
      'Das Aufgabencenter spiegelt dieselben Aufgaben wie FORQ Signal (die Glocke).',
      'Ein Klick auf eine Aufgabe springt direkt zur Quelle.',
    ],
  },
  vorgaenge: {
    area: 'Vorgänge',
    summary: 'Die zentrale Vorgangszentrale für Service, Verkauf, Miete, interne Aufträge und Ersatzteile.',
    tips: [
      'Jeder Vorgang hat eine stabile URL (/vorgaenge/Nummer) und ist teilbar.',
      'Die Statusspur (FORQ Flow) im Datensatz zeigt den Fortschritt.',
      '„Bereit zur Abrechnung" übergibt den Vorgang an Finance.',
    ],
    playbook: {
      title: 'Vorgang anlegen',
      steps: [
        '„Vorgang erstellen" öffnen',
        'Vorgangsart wählen (Service, Verkauf, Miete …)',
        'Kunde und optionale Bezugsmaschine setzen',
        'Adressen und Grundkonditionen prüfen',
        'Positionen erfassen – Enter springt zum nächsten Feld – und speichern',
      ],
    },
  },
  kunden: {
    area: 'Kunden',
    summary: 'Kunden, Ansprechpartner und Anschriften – strikt mandantengetrennt.',
    tips: [
      'Kundennummern folgen dem Mandanten-Nummernkreis (1xxxx / 2xxxx / 3xxxx).',
      'Beim Anlegen prüft FORQ automatisch auf Dubletten.',
      'Deep-Link zum Datensatz: /kunden/Nummer.',
    ],
  },
  maschinen: {
    area: 'Maschinen',
    summary: 'Maschinen- und Gerätestamm mit Seriennummern, Typen und Kundenbezug.',
    tips: [
      'Fällige UVV-Prüfungen erscheinen als Aufgabe in FORQ Signal.',
      'Deep-Link zum Datensatz: /maschinen/Nummer.',
    ],
  },
  lager: {
    area: 'Lager',
    summary: 'Bestände, Bewegungen, Lagerplätze und Beschaffung.',
    tips: [
      'Die Abschnitte sind einzeln deep-linkbar (/lager/bestaende, /lager/beschaffung …).',
      'Negativbestand ist nur mit entsprechendem Recht möglich.',
    ],
  },
  verkauf: {
    area: 'Verkauf',
    summary: 'Die Vertriebspipeline von der Anfrage bis zur Auslieferung.',
    tips: [
      'Angebote über der Freigabegrenze brauchen eine kaufmännische Freigabe.',
      'Jede Ansicht ist deep-linkbar (/verkauf/angebote, /verkauf/pipeline …).',
    ],
  },
  artikel: {
    area: 'Stammdaten',
    summary: 'Artikel, Dienstleistungen und Lieferanten als gemeinsame Stammdaten.',
    tips: [
      'Artikel, Dienstleistungen und Lieferanten teilen dieselbe Oberfläche.',
      'Einkaufspreise sind nur mit entsprechendem Recht sichtbar.',
    ],
  },
  finance: {
    area: 'Finance',
    summary: 'Ausgangsrechnungen, Freigaben und die Übergabe aus den Vorgängen.',
    tips: [
      'Vorgänge im Status „Bereit zur Abrechnung" landen hier zur Übergabe.',
      'Freigaben und Stornos sind an eigene Rechte gebunden.',
    ],
  },
};

const fallbackContext: DockContext = {
  area: 'FORQ',
  summary: 'Ein modulares ERP mit einer einheitlichen Shell – überall dieselben Bausteine.',
  tips: [
    'Strg + K öffnet die globale Suche (FORQ Command).',
    'Die Glocke oben rechts zeigt offene Aufgaben (FORQ Signal).',
    'Jede Seite und jeder Datensatz hat eine stabile, teilbare URL.',
  ],
};

/** Resolves the FORQY Dock context from the current pathname. */
export function resolveDockContext(pathname: string): DockContext {
  const segment = pathname.split('/').filter(Boolean)[0] ?? '';
  return contextBySegment[segment] ?? fallbackContext;
}

/** Global keyboard shortcuts, shown in every context. */
export const dockShortcuts: readonly DockShortcut[] = [
  { keys: ['Strg', 'K'], label: 'FORQ Command – global suchen und springen' },
  { keys: ['Esc'], label: 'Dialoge, Paletten und Panels schließen' },
  { keys: ['Enter'], label: 'In Tabellen und Editoren zum nächsten Feld' },
];

/** What's new in FORQ — reflects the actually shipped shell regions. */
export const dockPatchNotes: readonly DockPatchNote[] = [
  { title: 'FORQ Command', body: 'Globale, mandanten- und rechtebewusste Suche (Strg + K) mit Deep-Links zum Datensatz.' },
  { title: 'FORQ Signal', body: 'Echtes Aufgabencenter an der Glocke: abrechnen, freigeben, UVV, nachfassen – mit Sprung zur Quelle.' },
  { title: 'FORQ Flow', body: 'Statusspur direkt am Vorgang: erledigt → aktuell → offen.' },
];
